// Shared calculator types & pure functions for Tattoo Cost Suite

export type Placement = 'forearm' | 'chestback' | 'neck' | 'faceScalp' | 'ribsStomach'
export type Complexity = 'outline' | 'shaded' | 'color' | 'realism'
export type ArtistTier = 'apprentice' | 'professional' | 'elite'
export type Region = 'low' | 'standard' | 'high'

export const PLACEMENT_MULTIPLIERS: Record<Placement, { label: string, multiplier: number }> = {
  forearm: { label: 'Forearm / Outer Arm', multiplier: 1.0 },
  chestback: { label: 'Chest / Back', multiplier: 1.15 },
  neck: { label: 'Neck', multiplier: 1.25 },
  faceScalp: { label: 'Face / Scalp', multiplier: 1.3 },
  ribsStomach: { label: 'Ribs / Stomach', multiplier: 1.3 },
}

export const COMPLEXITY_MULTIPLIERS: Record<Complexity, { label: string, multiplier: number }> = {
  outline: { label: 'Outlines Only', multiplier: 0.8 },
  shaded: { label: 'Shaded Black & Grey', multiplier: 1.0 },
  color: { label: 'Vibrant Full Color', multiplier: 1.2 },
  realism: { label: 'Photo-Realism / Portrait', multiplier: 1.4 },
}

export const ARTIST_TIERS: Record<ArtistTier, { label: string, rate: number }> = {
  apprentice: { label: 'Apprentice', rate: 90 },
  professional: { label: 'Professional Studio', rate: 160 },
  elite: { label: 'Elite Specialist', rate: 270 },
}

export const REGION_MULTIPLIERS: Record<Region, { label: string, multiplier: number }> = {
  low: { label: 'Low Cost Area', multiplier: 0.85 },
  standard: { label: 'Standard Metro', multiplier: 1.0 },
  high: { label: 'High Cost City', multiplier: 1.3 },
}

export const STUDIO_MINIMUM = 80
export const BASE_RATE_PER_SQ_INCH = 12 // baseline minutes-driven rate reference

export interface TattooCalcInput {
  widthIn: number
  heightIn: number
  placement: Placement
  complexity: Complexity
  artistTier: ArtistTier
  region: Region
}

export interface TattooCalcResult {
  squareInches: number
  estimatedHours: number
  hourlyRate: number
  laborCost: number
  materialsFee: number
  subtotal: number
  recommendedTip: number
  total: number
  meetsMinimum: boolean
}

export function calculateTattooCost(input: TattooCalcInput): TattooCalcResult {
  const squareInches = Math.max(0, input.widthIn * input.heightIn)
  // Base hours: roughly 0.35 hr per sq inch at baseline, scaled by complexity & placement difficulty
  const baseHoursPerSqIn = 0.35
  const placementMult = PLACEMENT_MULTIPLIERS[input.placement].multiplier
  const complexityMult = COMPLEXITY_MULTIPLIERS[input.complexity].multiplier
  const regionMult = REGION_MULTIPLIERS[input.region].multiplier
  const hourlyRate = ARTIST_TIERS[input.artistTier].rate

  const estimatedHours = Math.max(0.5, squareInches * baseHoursPerSqIn * placementMult * complexityMult)
  const laborCost = estimatedHours * hourlyRate * regionMult
  const materialsFee = Math.max(15, squareInches * 1.2)
  const subtotal = laborCost + materialsFee
  const meetsMinimum = subtotal >= STUDIO_MINIMUM
  const total = meetsMinimum ? subtotal : STUDIO_MINIMUM
  const recommendedTip = total * 0.18

  return {
    squareInches: Math.round(squareInches * 100) / 100,
    estimatedHours: Math.round(estimatedHours * 10) / 10,
    hourlyRate,
    laborCost: Math.round(laborCost),
    materialsFee: Math.round(materialsFee),
    subtotal: Math.round(subtotal),
    recommendedTip: Math.round(recommendedTip),
    total: Math.round(total),
    meetsMinimum,
  }
}

export type InkColor = 'black' | 'dark' | 'vibrant'
export type TattooAge = 'under1' | 'oneToFive' | 'fivePlus'
export type SkinTone = 'typeI_II' | 'typeIII_IV' | 'typeV_VI'
export type LaserTech = 'nanosecond' | 'picosecond'

export const INK_COLOR_DIFFICULTY: Record<InkColor, { label: string, multiplier: number }> = {
  black: { label: 'Black Ink', multiplier: 1.0 },
  dark: { label: 'Dark Pigments (Blue/Green)', multiplier: 1.25 },
  vibrant: { label: 'Vibrant Multi-Color / Neon', multiplier: 1.5 },
}

export const TATTOO_AGE_SESSIONS: Record<TattooAge, { label: string, sessionDelta: number }> = {
  under1: { label: 'Under 1 Year Old', sessionDelta: 2 },
  oneToFive: { label: '1-5 Years Old', sessionDelta: 0 },
  fivePlus: { label: '5+ Years, Faded', sessionDelta: -2 },
}

export const SKIN_TONE_MULTIPLIERS: Record<SkinTone, { label: string, multiplier: number, warning?: string }> = {
  typeI_II: { label: 'Type I-II (Fair Skin)', multiplier: 1.0 },
  typeIII_IV: { label: 'Type III-IV (Medium Skin)', multiplier: 1.15 },
  typeV_VI: {
    label: 'Type V-VI (Deep Melanin)',
    multiplier: 1.35,
    warning: 'Higher melanin skin carries increased risk of pigment scarring. Picosecond (Pico) lasers are strongly recommended over Q-Switched to minimize this risk.',
  },
}

export const LASER_TECH: Record<LaserTech, { label: string, baseRatePerSqIn: number, sessionMultiplier: number }> = {
  nanosecond: { label: 'Nanosecond Q-Switched', baseRatePerSqIn: 12, sessionMultiplier: 1.4 },
  picosecond: { label: 'Picosecond (PicoWay / PicoSure)', baseRatePerSqIn: 18, sessionMultiplier: 0.85 },
}

export interface LaserCalcInput {
  squareInches: number
  inkColor: InkColor
  tattooAge: TattooAge
  skinTone: SkinTone
  tech: LaserTech
}

export interface LaserCalcResult {
  baseSessions: number
  totalSessions: number
  costPerSession: number
  totalCost: number
  warning?: string
}

export function calculateLaserRemoval(input: LaserCalcInput): LaserCalcResult {
  const sqIn = Math.max(0, input.squareInches)
  const techInfo = LASER_TECH[input.tech]
  const inkMult = INK_COLOR_DIFFICULTY[input.inkColor].multiplier
  const skinInfo = SKIN_TONE_MULTIPLIERS[input.skinTone]
  const ageDelta = TATTOO_AGE_SESSIONS[input.tattooAge].sessionDelta

  // Baseline sessions ~6-8 for average tattoo, scaled by ink difficulty & skin tone & tech
  const baselineSessions = 7
  let totalSessions = baselineSessions * inkMult * skinInfo.multiplier * techInfo.sessionMultiplier + ageDelta
  totalSessions = Math.max(2, Math.round(totalSessions))

  const costPerSession = Math.round(sqIn * techInfo.baseRatePerSqIn)
  const totalCost = Math.round(costPerSession * totalSessions)

  return {
    baseSessions: baselineSessions,
    totalSessions,
    costPerSession,
    totalCost,
    warning: skinInfo.warning,
  }
}
